import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, useToast, errText, useCaptchaConfirm, TenantSelect } from '../ui';

// Standalone Forms & Modules (Seed) management — reachable two ways:
//  - /admin/tenants/:clientCode/seed          (linked from Tenant Detail)
//  - /admin/tenants/tools/seed                (linked from the sidebar, no
//    clientCode yet — shows a lookup box first)
export default function SeedManagement() {
  const { clientCode: routeClientCode } = useParams();
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  const [clientCode, setClientCode] = useState(routeClientCode || '');
  const [tenantDbName, setTenantDbName] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (cc) => {
    setStatus(null);
    try {
      const tenant = await tenantApi.getTenant(cc);
      const dbRes = await tenantApi.getDatabase(tenant.data.clientName);
      const db = dbRes.tenantDbName;
      setTenantDbName(db);
      const res = await tenantApi.getSeedStatus(db);
      setStatus(res);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  useEffect(() => {
    if (clientCode) load(clientCode);
  }, [clientCode]); // eslint-disable-line

  const handleReseed = async () => {
    setBusy(true);
    try {
      const res = await tenantApi.runSeed(tenantDbName);
      toast.success(res.msg || 'Seeded.');
      load(clientCode);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    const ok = await confirm(
      'Clear all reference/seed data (schema forms & modules) for this tenant? You will need to re-seed before the tenant app is usable again.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await tenantApi.resetSeed(tenantDbName);
      toast.success(res.msg || 'Reference data cleared.');
      load(clientCode);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {confirmNode}
      <h1 className="page-title">forms_and_modules</h1>
      <p className="page-sub">
        {routeClientCode && (
          <>
            <Link to={`/admin/tenants/${routeClientCode}`} style={{ color: 'var(--text-dim)' }}>← {routeClientCode}</Link>
            {'  /  '}
          </>
        )}
        GET/POST/DELETE /v1/admin/tenants/seed — schema tables, forms, and modules seeded into a tenant DB
      </p>

      {!routeClientCode && (
        <Panel title="// LOOKUP_TENANT">
          <div className="toolbar">
            <div className="toolbar__group">
              <TenantSelect value={clientCode} onSelect={(cc) => setClientCode(cc)} />
            </div>
          </div>
        </Panel>
      )}

      {clientCode && (
        <Panel title={`// SEED_STATUS — ${tenantDbName || clientCode}`}>
          {status === null && <Loader label="fetching seed status" />}
          {status !== null && (
            <>
              <div className="kv-grid" style={{ marginBottom: 18 }}>
                <dt>totalTables</dt><dd>{status.totalTables}</dd>
                <dt>formCount</dt><dd>{status.formCount}</dd>
                <dt>moduleCount</dt><dd>{status.moduleCount}</dd>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="term-btn term-btn--solid" disabled={busy} onClick={handleReseed}>
                  {busy ? 'running...' : 're_seed'}
                </button>
                <button className="term-btn term-btn--danger" disabled={busy} onClick={handleReset}>
                  {busy ? 'clearing...' : 'clear_reference_data'}
                </button>
              </div>
              <p className="field-hint" style={{ marginTop: 12 }}>
                re_seed runs POST /seed again (safe to repeat) · clear_reference_data runs DELETE /seed —
                the tenant app has no forms/modules until you re_seed after clearing.
              </p>
            </>
          )}
        </Panel>
      )}
    </>
  );
}
